/**
 * Heatmap report (ADR 0024): where safety events happened ("events") and where vehicles were moving
 * ("usage"). The aggregation runs in SQL (supabase/migrations/20270405000001_heatmap.sql, report_heatmap,
 * SECURITY INVOKER, RLS applies) into a grid of ~4 m cells; the console only receives cells and totals.
 * This module holds the shared limits, the result shape and a pure copy of the grid (used by the demo mode
 * and tested against the SQL formula). Pure TypeScript.
 */
import type { AlarmType } from "./alarms.js";
import { EVENT_GROUPS, type EventGroup } from "./reports.js";

export const HEATMAP_MODES = ["events", "usage"] as const;
export type HeatmapMode = (typeof HEATMAP_MODES)[number];

export const HEATMAP_LIMITS = Object.freeze({
  /** Longest date range (local days, inclusive). SQL: report_check(…, 31). */
  maxDays: 31,
  /** Usage mode: vehicles × days (e.g. 12 vehicles × 31 days) until a load test on managed Postgres. SQL: fts:report_too_large. */
  maxVehicleDays: 400,
  /** Cells returned (the hottest first). */
  maxCells: 20_000,
  /** Cell edge in metres. */
  cellM: 4,
  /** Usage mode: a point counts when ignition is on and speed >= this (live map / usage rule). */
  movingKmh: 1,
  /** Usage mode: GNSS accuracy limit when the device reports one. */
  maxAccM: 15,
});

/**
 * Event groups that can carry a location (checklist alarms never have one). Same groups as the event report,
 * so a KPI tile or a link can pass the same "g" filter.
 */
export const HEATMAP_EVENT_GROUPS: readonly EventGroup[] = EVENT_GROUPS.filter((g) => g !== "checklist" && g !== "device_health");

/** Weight of one located alarm: impacts by impact level (1–3), every other type 1. */
export function heatmapEventWeight(type: AlarmType, severity: number): number {
  return type === "impact" ? Math.max(1, Math.min(3, Math.round(severity))) : 1;
}

/** One grid cell: its centre, the summed weight and the number of points / events in it. */
export type HeatmapCell = { lat: number; lon: number; weight: number; count: number };

/** report_heatmap(): cells (hottest first, at most maxCells) and totals over ALL cells. */
export type HeatmapResult = {
  tz: string;
  mode: HeatmapMode;
  cellM: number;
  cells: HeatmapCell[];
  /** Cells before the cap; `truncated` when more than maxCells. */
  totalCells: number;
  truncated: boolean;
  /** Points (usage) or located events (events). */
  points: number;
  weight: number;
  maxWeight: number;
  /** Vehicles with at least one point / located event. */
  vehicles: number;
  /** Events without a location (events mode; usage: points without a valid fix are not counted at all). */
  unlocated: number;
  /** [west, south, east, north] of the cell centres; null without cells. */
  bounds: [number, number, number, number] | null;
};

/** Metres per degree of latitude (R = 6 371 008.8 m), as in SQL. */
export const M_PER_DEG = 111195.0798;

/**
 * Same grid as report_heatmap(): rows of `cellM` metres of latitude; columns of `cellM` metres at the middle
 * of each 1-degree latitude band. Returns the cell key and centre of a position.
 */
export function heatmapCellOf(lat: number, lon: number, cellM: number = HEATMAP_LIMITS.cellM): { key: string; lat: number; lon: number } {
  const band = Math.floor(lat);
  const k = (M_PER_DEG * Math.cos(((band + 0.5) * Math.PI) / 180)) / cellM;
  const iy = Math.floor((lat * M_PER_DEG) / cellM);
  const ix = Math.floor(lon * k);
  return { key: `${band}|${iy}|${ix}`, lat: ((iy + 0.5) * cellM) / M_PER_DEG, lon: (ix + 0.5) / k };
}

/**
 * Aggregates weighted points into cells (hottest first, capped) — the demo mode's copy of the SQL. A point may
 * stand for several samples (`count`, default 1).
 */
export function heatmapAggregate(
  points: Iterable<{ lat: number; lon: number; weight?: number; count?: number; vehicleId?: string }>,
  opts: { mode: HeatmapMode; tz: string; unlocated?: number; cellM?: number; maxCells?: number },
): HeatmapResult {
  const cellM = opts.cellM ?? HEATMAP_LIMITS.cellM;
  const max = opts.maxCells ?? HEATMAP_LIMITS.maxCells;
  const cells = new Map<string, HeatmapCell>();
  const vehicles = new Set<string>();
  for (const p of points) {
    if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon) || Math.abs(p.lat) > 85 || Math.abs(p.lon) > 180 || (p.lat === 0 && p.lon === 0)) continue;
    const c = heatmapCellOf(p.lat, p.lon, cellM);
    const cur = cells.get(c.key);
    if (cur) {
      cur.weight += p.weight ?? 1;
      cur.count += p.count ?? 1;
    } else cells.set(c.key, { lat: c.lat, lon: c.lon, weight: p.weight ?? 1, count: p.count ?? 1 });
    if (p.vehicleId) vehicles.add(p.vehicleId);
  }
  const all = [...cells.values()].sort((a, b) => b.weight - a.weight || b.count - a.count || a.lat - b.lat || a.lon - b.lon);
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity, sum = 0, pts = 0;
  for (const c of all) {
    w = Math.min(w, c.lon);
    e = Math.max(e, c.lon);
    s = Math.min(s, c.lat);
    n = Math.max(n, c.lat);
    sum += c.weight;
    pts += c.count;
  }
  return {
    tz: opts.tz,
    mode: opts.mode,
    cellM,
    cells: all.slice(0, max),
    totalCells: all.length,
    truncated: all.length > max,
    points: pts,
    weight: sum,
    maxWeight: all[0]?.weight ?? 0,
    vehicles: vehicles.size,
    unlocated: opts.unlocated ?? 0,
    bounds: all.length ? [w, s, e, n] : null,
  };
}
